<template>
  <main class="flex-1 pb-6 px-6 pt-3">
    <!-- Header -->
    <div class="bg-background border-b border-border px-8 py-2 -mx-6 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-xl font-bold text-foreground">Subscription Plans</h1>
          <p class="text-muted-foreground text-sm mt-1">
            Manage subscription plans for your customers
          </p>
        </div>
      <div class="flex items-center space-x-3">
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 font-semibold"
          @click="createCustomer"
        >
          Create Customer
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-10 px-4 py-2 bg-foreground hover:bg-foreground/90 text-background font-semibold shadow-lg"
          @click="createSubscription"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-plus mr-2 h-4 w-4"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          Create Subscription
        </button>
      </div>
      </div>
    </div>
    <div class="px-2">

      <!-- Tabs -->
      <div dir="ltr" data-orientation="horizontal" class="w-full">
      <div
        role="tablist"
        aria-orientation="horizontal"
        class="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground mb-6"
        tabindex="0"
        data-orientation="horizontal"
        style="outline: none;"
      >
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'plans' ? 'true' : 'false'"
            aria-controls="plans-tab"
            :data-state="activeTab === 'plans' ? 'active' : 'inactive'"
            class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            tabindex="-1"
            data-orientation="horizontal"
            @click="activeTab = 'plans'"
          >
            Plans
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'customers' ? 'true' : 'false'"
            aria-controls="customers-tab"
            :data-state="activeTab === 'customers' ? 'active' : 'inactive'"
            class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
            tabindex="-1"
            data-orientation="horizontal"
            @click="activeTab = 'customers'"
          >
            Current Customers
          </button>
        </div>

        <!-- Plans Tab -->
        <div
          v-show="activeTab === 'plans'"
          id="plans-tab"
          data-state="active"
          data-orientation="horizontal"
          role="tabpanel"
          tabindex="0"
          class="mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 space-y-6"
        >
          <!-- Stats Cards (exact layout as provided HTML) -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-muted-foreground">
                    Monthly Recurring Revenue
                  </p>
                  <h3 class="text-3xl font-bold mt-2">
                    {{ formattedTotalMRR }}
                  </h3>
                  <div class="flex items-center mt-2 text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-trending-up h-4 w-4 text-success mr-1"
                    >
                      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                      <polyline points="16 7 22 7 22 13"></polyline>
                    </svg>
                    <span class="text-success font-medium">
                      +12.5%
                    </span>
                    <span class="text-muted-foreground ml-1">
                      from last month
                    </span>
                  </div>
                </div>
                <div class="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-dollar-sign h-6 w-6 text-primary"
                  >
                    <line x1="12" x2="12" y1="2" y2="22"></line>
                    <path
                      d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                    ></path>
                  </svg>
                </div>
              </div>
            </div>
            <div class="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-muted-foreground">
                    Total Customers
                  </p>
                  <h3 class="text-3xl font-bold mt-2">
                    {{ totalCustomers }}
                  </h3>
                  <div class="flex items-center mt-2 text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-trending-up h-4 w-4 text-success mr-1"
                    >
                      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                      <polyline points="16 7 22 7 22 13"></polyline>
                    </svg>
                    <span class="text-success font-medium">
                      +3
                    </span>
                    <span class="text-muted-foreground ml-1">
                      from last month
                    </span>
                  </div>
                </div>
                <div class="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-users h-6 w-6 text-primary"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
              </div>
            </div>
            <div class="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-muted-foreground">
                    Churn Rate
                  </p>
                  <h3 class="text-3xl font-bold mt-2">
                    {{ churnRateMetric.toFixed(1) }}%
                  </h3>
                  <div class="flex items-center mt-2 text-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-trending-down h-4 w-4 text-success mr-1"
                    >
                      <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
                      <polyline points="16 17 22 17 22 11"></polyline>
                    </svg>
                    <span class="text-success font-medium">
                      -0.8%
                    </span>
                    <span class="text-muted-foreground ml-1">
                      from last month
                    </span>
                  </div>
                </div>
                <div class="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-trending-down h-6 w-6 text-destructive"
                  >
                    <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
                    <polyline points="16 17 22 17 22 11"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Table Card -->
          <div class="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
            <!-- Table Header / Controls -->
            <div class="p-6 border-b border-border">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                  <div class="flex items-center space-x-2">
                    <span class="text-sm text-muted-foreground">Show</span>
                    <select
                      v-model.number="queries.per_page"
                      class="h-9 rounded-md border border-input bg-background px-3 text-sm"
                      @change="onPerPageChange"
                    >
                      <option v-for="option in [10, 25, 50, 100]" :key="option" :value="option">
                        {{ option }}
                      </option>
                    </select>
                    <span class="text-sm text-muted-foreground">entries</span>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-sm text-muted-foreground">Search:</span>
                  <div class="relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                    <input
                      v-model="queries.search"
                      type="text"
                      class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-9 w-64"
                      placeholder="Search..."
                      @keyup.enter="getSubscriptions"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="relative w-full overflow-auto">
              <table class="w-full caption-bottom text-sm">
                <thead class="[&_tr]:border-b">
                  <tr class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50 bg-muted/50">
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Name
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Type
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Interval
                    </th>
                    <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-center">
                      Active Customers
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      MRR
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Churn Rate
                    </th>
                    <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody class="[&_tr:last-child]:border-0">
                  <tr v-if="loading">
                    <td colspan="8" class="p-4 text-center">
                      <b-spinner label="Loading..." variant="primary" small />
                    </td>
                  </tr>
                  <tr
                    v-else-if="!subscriptions.length"
                    class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/30"
                  >
                    <td colspan="8" class="p-4 text-center text-muted-foreground">
                      No subscriptions found.
                    </td>
                  </tr>
                  <tr
                    v-for="sub in subscriptions"
                    v-else
                    :key="sub.id"
                    class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/30"
                  >
                    <td class="p-4 align-middle font-medium">
                      {{ sub.name }}
                    </td>
                    <td class="p-4 align-middle">
                      <div
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-success/10 text-success border-success/20"
                      >
                        {{ sub.mode || 'Recurring' }}
                      </div>
                    </td>
                    <td class="p-4 align-middle font-medium">
                      {{ formatCurrency(sub.amount) }}
                    </td>
                    <td class="p-4 align-middle text-muted-foreground">
                      {{ sub.interval || 'Month' }}
                    </td>
                    <td class="p-4 align-middle text-center">
                      <div class="flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-users h-4 w-4 mr-1.5 text-muted-foreground"
                        >
                          <path
                            d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                          />
                          <circle cx="9" cy="7" r="4" />
                          <path
                            d="M22 21v-2a4 4 0 0 0-3-3.87"
                          />
                          <path
                            d="M16 3.13a4 4 0 0 1 0 7.75"
                          />
                        </svg>
                        <span class="font-medium">
                          {{ sub.active_customers || 0 }}
                        </span>
                      </div>
                    </td>
                    <td class="p-4 align-middle font-semibold text-success">
                      {{ formatCurrency(sub.mrr || sub.amount) }}
                    </td>
                    <td class="p-4 align-middle">
                      <div class="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-trending-down h-4 w-4 mr-1 text-destructive"
                        >
                          <polyline
                            points="22 17 13.5 8.5 8.5 13.5 2 7"
                          />
                          <polyline points="16 17 22 17 22 11" />
                        </svg>
                        <span class="text-muted-foreground">
                          {{ (sub.churn_rate || churnRateMetric).toFixed(1) }}%
                        </span>
                      </div>
                    </td>
                    <td class="p-4 align-middle text-right">
                      <div class="flex items-center justify-end space-x-2">
                        <button
                          type="button"
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-8 px-3"
                          @click="editSubscription(sub)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-square-pen h-3 w-3 mr-1.5"
                          >
                            <path
                              d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            />
                            <path
                              d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          type="button"
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background rounded-md h-8 px-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                          @click="confirmCancel(sub)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-trash2 h-3 w-3 mr-1.5"
                          >
                            <path d="M3 6h18" />
                            <path
                              d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"
                            />
                            <path
                              d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"
                            />
                            <line x1="10" x2="10" y1="11" y2="17" />
                            <line x1="14" x2="14" y1="11" y2="17" />
                          </svg>
                          Delete
                        </button>
                        <button
                          type="button"
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-8 px-3"
                          @click="viewCheckout(sub)"
                        >
                          View Checkout
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="p-4 border-t border-border flex items-center justify-between">
              <div class="text-sm text-muted-foreground">
                Total {{ totalSubscriptions }} plan<span v-if="totalSubscriptions !== 1">s</span>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="queries.page === 1"
                  @click="onPageChanged(queries.page - 1)"
                >
                  «
                </button>
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="queries.page === 1"
                  @click="onPageChanged(queries.page - 1)"
                >
                  ‹
                </button>
                <button
                  v-for="page in totalPages"
                  :key="page"
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 rounded-md px-3"
                  :class="page === queries.page ? 'text-primary-foreground hover:bg-primary/90 bg-primary' : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'"
                  @click="onPageChanged(page)"
                >
                  {{ page }}
                </button>
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="queries.page === totalPages"
                  @click="onPageChanged(queries.page + 1)"
                >
                  ›
                </button>
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="queries.page === totalPages"
                  @click="onPageChanged(totalPages)"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Customers Tab -->
        <div
          v-show="activeTab === 'customers'"
          id="customers-tab"
          data-state="active"
          data-orientation="horizontal"
          role="tabpanel"
          tabindex="0"
          class="mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <div class="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
            <!-- Table Header / Controls -->
            <div class="p-6 border-b border-border">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                  <div class="flex items-center space-x-2">
                    <span class="text-sm text-muted-foreground">Show</span>
                    <select
                      v-model.number="customerQueries.per_page"
                      class="h-9 rounded-md border border-input bg-background px-3 text-sm"
                      @change="onCustomerPerPageChange"
                    >
                      <option v-for="option in [10, 25, 50, 100]" :key="option" :value="option">
                        {{ option }}
                      </option>
                    </select>
                    <span class="text-sm text-muted-foreground">entries</span>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-sm text-muted-foreground">Search:</span>
                  <div class="relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                    <input
                      v-model="customerQueries.search"
                      type="text"
                      class="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-9 w-64"
                      placeholder="Search customers..."
                      @keyup.enter="getCustomerSubscriptions"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="relative w-full overflow-auto">
              <table class="w-full caption-bottom text-sm">
                <thead class="[&_tr]:border-b">
                  <tr class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50 bg-muted/50">
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Customer
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Plan
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Status
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Start Date
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Next Billing
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      MRR
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Lifetime Value
                    </th>
                    <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                      Payment Method
                    </th>
                    <th class="h-12 px-4 align-middle font-medium text-muted-foreground text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody class="[&_tr:last-child]:border-0">
                  <tr v-if="loadingCustomers">
                    <td colspan="9" class="p-4 text-center">
                      <b-spinner label="Loading..." variant="primary" small />
                    </td>
                  </tr>
                  <tr
                    v-else-if="!customerSubscriptions.length"
                    class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/30"
                  >
                    <td colspan="9" class="p-4 text-center text-muted-foreground">
                      No customers found.
                    </td>
                  </tr>
                  <tr
                    v-for="customer in customerSubscriptions"
                    v-else
                    :key="customer.id"
                    class="border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/30"
                  >
                    <td class="p-4 align-middle">
                      <div>
                        <div class="font-medium">{{ customer.name }}</div>
                        <div class="text-sm text-muted-foreground">{{ customer.email }}</div>
                      </div>
                    </td>
                    <td class="p-4 align-middle font-medium">
                      {{ customer.plan_name || 'N/A' }}
                    </td>
                    <td class="p-4 align-middle">
                      <div
                        v-if="customer.status === 'active' || customer.status === 'Active'"
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-success/10 text-success border-success/20"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-circle-check-big h-3 w-3 mr-1"
                        >
                          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                          <path d="m9 11 3 3L22 4"></path>
                        </svg>
                        Active
                      </div>
                      <div
                        v-else
                        class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-destructive/10 text-destructive border-destructive/20"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          class="lucide lucide-circle-alert h-3 w-3 mr-1"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" x2="12" y1="8" y2="12"></line>
                          <line x1="12" x2="12.01" y1="16" y2="16"></line>
                        </svg>
                        Past Due
                      </div>
                    </td>
                    <td class="p-4 align-middle text-muted-foreground">
                      {{ formatDate(customer.start_date) }}
                    </td>
                    <td class="p-4 align-middle text-muted-foreground">
                      {{ formatDate(customer.next_billing_date) }}
                    </td>
                    <td class="p-4 align-middle font-medium">
                      {{ formatCurrency(customer.mrr || customer.amount) }}
                    </td>
                    <td class="p-4 align-middle font-semibold text-primary">
                      {{ formatCurrency(customer.lifetime_value || 0) }}
                    </td>
                    <td class="p-4 align-middle text-muted-foreground font-mono text-xs">
                      {{ customer.payment_method || 'N/A' }}
                    </td>
                    <td class="p-4 align-middle text-right">
                      <div class="flex items-center justify-end space-x-2">
                        <button
                          type="button"
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-8 px-3"
                          @click="editCustomerSubscription(customer)"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-square-pen h-3 w-3 mr-1.5"
                          >
                            <path
                              d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                            />
                            <path
                              d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          type="button"
                          class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md h-8 px-3"
                          @click="viewInStripe(customer)"
                        >
                          View in Stripe
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="p-4 border-t border-border flex items-center justify-between">
              <div class="text-sm text-muted-foreground">
                Total {{ totalCustomerSubscriptions }} customer<span v-if="totalCustomerSubscriptions !== 1">s</span>
              </div>
              <div class="flex items-center space-x-2">
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="customerQueries.page === 1"
                  @click="onCustomerPageChanged(customerQueries.page - 1)"
                >
                  «
                </button>
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="customerQueries.page === 1"
                  @click="onCustomerPageChanged(customerQueries.page - 1)"
                >
                  ‹
                </button>
                <button
                  v-for="page in totalCustomerPages"
                  :key="page"
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 rounded-md px-3"
                  :class="page === customerQueries.page ? 'text-primary-foreground hover:bg-primary/90 bg-primary' : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'"
                  @click="onCustomerPageChanged(page)"
                >
                  {{ page }}
                </button>
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="customerQueries.page === totalCustomerPages"
                  @click="onCustomerPageChanged(customerQueries.page + 1)"
                >
                  ›
                </button>
                <button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-3"
                  :disabled="customerQueries.page === totalCustomerPages"
                  @click="onCustomerPageChanged(totalCustomerPages)"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Subscription Modal -->
    <b-modal
      v-model="showCancelConfirm"
      title="Cancel Subscription"
      :ok-disabled="canceling"
      @ok="cancelSubscription"
    >
      <template v-if="canceling">
        <b-spinner small type="grow" class="mr-2" />
        Cancelling subscription...
      </template>
      <template v-else>
        Are you sure you want to cancel this subscription?
      </template>
    </b-modal>
  </main>
</template>

<script>
export default {
  data() {
    return {
      activeTab: 'plans',
      subscriptions: [],
      allSubscriptions: [], // Store all subscriptions for stats calculation
      queries: {
        page: 1,
        per_page: 10,
        search: '',
      },
      totalSubscriptions: 0,
      customerSubscriptions: [],
      customerQueries: {
        page: 1,
        per_page: 10,
        search: '',
      },
      totalCustomerSubscriptions: 0,
      stats: {
        totalMRR: 0,
        totalCustomers: 0,
        churnRate: 0,
      },
      showCancelConfirm: false,
      cancelingSubscription: null,
      canceling: false,
      searching: false,
      loading: false,
      loadingStats: false,
      loadingCustomers: false,
    };
  },
  computed: {
    totalMRR() {
      // Calculate MRR from all subscriptions
      return this.allSubscriptions.reduce((sum, sub) => {
        const mrr = parseFloat(sub.mrr || sub.amount || 0);
        const interval = sub.interval || sub.agency_subscription?.interval || 'month';
        
        // Convert to monthly if needed
        let monthlyAmount = mrr;
        if (interval === 'year') {
          monthlyAmount = mrr / 12;
        } else if (interval === 'day') {
          monthlyAmount = mrr * 30;
        } else if (interval === 'week') {
          monthlyAmount = mrr * 4.33;
        }
        
        return sum + (isNaN(monthlyAmount) ? 0 : monthlyAmount);
      }, 0);
    },
    formattedTotalMRR() {
      return this.formatCurrency(this.totalMRR);
    },
    totalCustomers() {
      // Count unique users with active subscriptions
      const uniqueUsers = new Set();
      this.allSubscriptions.forEach(sub => {
        if (sub.user_id && sub.status !== 'canceled') {
          uniqueUsers.add(sub.user_id);
        }
      });
      return uniqueUsers.size || this.totalSubscriptions;
    },
    churnRateMetric() {
      // Calculate churn rate: canceled subscriptions / total subscriptions
      if (this.allSubscriptions.length === 0) return 0;
      
      const canceledCount = this.allSubscriptions.filter(
        sub => sub.status === 'canceled' || sub.status === 'cancelled'
      ).length;
      
      const totalCount = this.allSubscriptions.length;
      if (totalCount === 0) return 0;
      
      return (canceledCount / totalCount) * 100;
    },
    totalPages() {
      if (!this.totalSubscriptions || !this.queries.per_page) return 1;
      return Math.max(1, Math.ceil(this.totalSubscriptions / this.queries.per_page));
    },
    totalCustomerPages() {
      if (!this.totalCustomerSubscriptions || !this.customerQueries.per_page) return 1;
      return Math.max(1, Math.ceil(this.totalCustomerSubscriptions / this.customerQueries.per_page));
    },
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'customers' && this.customerSubscriptions.length === 0) {
        this.getCustomerSubscriptions();
      }
    },
  },
  created() {
    this.getSubscriptions();
    this.getStats();
    this.getCustomerSubscriptions();
  },
  methods: {
    getSubscriptions() {
      this.searching = true;
      this.loading = true;
      this.$store.dispatch('agencySubscription/fetchSubscriptions', this.queries).then(res => {
        // Defensive: handle undefined or unexpected API response
        if (res && Array.isArray(res.data)) {
          this.subscriptions = res.data.map(sub => {
            let name = '';
            if (sub.user?.full_name) {
              name = sub.user.full_name;
            } else if (sub.user?.first_name || sub.user?.last_name) {
              name = [sub.user.first_name, sub.user.last_name].filter(Boolean).join(' ');
            }
            return {
              name: name,
              amount: sub.agency_subscription?.amount || sub.amount || 0,
            mrr: sub.mrr || sub.agency_subscription?.amount || sub.amount || 0,
            mode: sub.agency_subscription?.mode || sub.mode || 'Recurring',
            interval: sub.agency_subscription?.interval || sub.interval || 'month',
            id: sub.id,
            user_id: sub.user_id || sub.user?.id,
            status: sub.status || 'active',
            active_customers: sub.active_customers || 0,
            churn_rate: sub.churn_rate || 0,
            raw: sub
          };
          });
        } else {
          this.subscriptions = [];
        }
        this.totalSubscriptions = res && typeof res.total === 'number' ? res.total : 0;
      }).finally(() => {
        this.searching = false;
        this.loading = false;
      });
    },
    getStats() {
      // Fetch all subscriptions for stats calculation (without pagination)
      this.loadingStats = true;
      this.$store.dispatch('agencySubscription/fetchSubscriptions', { per_page: 1000 }).then(res => {
        if (res && Array.isArray(res.data)) {
          this.allSubscriptions = res.data.map(sub => ({
            amount: sub.agency_subscription?.amount || sub.amount || 0,
            mrr: sub.mrr || sub.agency_subscription?.amount || sub.amount || 0,
            interval: sub.agency_subscription?.interval || sub.interval || 'month',
            user_id: sub.user_id || sub.user?.id,
            status: sub.status || 'active',
            raw: sub
          }));
        } else {
          this.allSubscriptions = [];
        }
      }).catch(() => {
        this.allSubscriptions = [];
      }).finally(() => {
        this.loadingStats = false;
      });
    },
    confirmCancel(sub) {
      this.cancelingSubscription = sub.id;
      this.showCancelConfirm = true;
      this.canceling = false;
    },
    cancelSubscription() {
      if (!this.cancelingSubscription) return;
      this.canceling = true;
      this.$store.dispatch('agencySubscription/cancelSubscription', this.cancelingSubscription)
        .then(() => {
          this.getSubscriptions();
          this.getStats(); // Refresh stats after canceling
          this.showCancelConfirm = false;
          this.cancelingSubscription = null;
        })
        .finally(() => {
          this.canceling = false;
        });
    },
    onPerPageChange() {
      this.queries.page = 1;
      this.getSubscriptions();
    },
    onPageChanged(page) {
      if (page < 1 || page > this.totalPages) return;
      this.queries.page = page;
      this.getSubscriptions();
    },
    formatCurrency(value) {
      const amount = parseFloat(value || 0);
      if (isNaN(amount)) {
        return '$0.00';
      }
      return amount.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
      });
    },
    editSubscription(sub) {
      // Placeholder for future edit functionality
      // eslint-disable-next-line no-console
      console.log('Edit subscription', sub);
    },
    viewCheckout(sub) {
      // Placeholder for future view checkout functionality
      // eslint-disable-next-line no-console
      console.log('View checkout for subscription', sub);
    },
    createCustomer() {
      this.$router.push({ name: 'agency.customers.index' });
    },
    createSubscription() {
      this.$router.push({ name: 'agency.business-subscriptions-form' });
    },
    getCustomerSubscriptions() {
      this.loadingCustomers = true;
      this.$store.dispatch('agencySubscription/fetchSubscriptions', this.customerQueries).then(res => {
        if (res && Array.isArray(res.data)) {
          this.customerSubscriptions = res.data.map(sub => {
            let name = '';
            if (sub.user?.full_name) {
              name = sub.user.full_name;
            } else if (sub.user?.first_name || sub.user?.last_name) {
              name = [sub.user.first_name, sub.user.last_name].filter(Boolean).join(' ');
            }
            
            // Calculate lifetime value (MRR * months since start)
            const startDate = sub.created_at || sub.starts_at || sub.user_subscription?.created_at;
            const monthsActive = startDate ? this.getMonthsBetween(startDate, new Date()) : 0;
            const lifetimeValue = (parseFloat(sub.mrr || sub.amount || 0) * Math.max(1, monthsActive));
            
            // Format payment method
            let paymentMethod = 'N/A';
            if (sub.payment_method?.last4) {
              paymentMethod = `•••• ${sub.payment_method.last4}`;
            } else if (sub.stripe_payment_method_id) {
              paymentMethod = `•••• ${sub.stripe_payment_method_id.slice(-4)}`;
            }
            
            return {
              id: sub.id,
              name: name,
              email: sub.user?.email || '',
              plan_name: sub.agency_subscription?.name || sub.plan_name || 'N/A',
              status: sub.status || (sub.user_subscription?.status === 'canceled' ? 'canceled' : 'active'),
              start_date: startDate,
              next_billing_date: sub.next_billing_date || sub.user_subscription?.next_billing_date || sub.current_period_end,
              mrr: sub.mrr || sub.amount || sub.agency_subscription?.amount || 0,
              amount: sub.amount || sub.agency_subscription?.amount || 0,
              lifetime_value: lifetimeValue,
              payment_method: paymentMethod,
              raw: sub
            };
          });
        } else {
          this.customerSubscriptions = [];
        }
        this.totalCustomerSubscriptions = res && typeof res.total === 'number' ? res.total : 0;
      }).catch(() => {
        this.customerSubscriptions = [];
        this.totalCustomerSubscriptions = 0;
      }).finally(() => {
        this.loadingCustomers = false;
      });
    },
    onCustomerPerPageChange() {
      this.customerQueries.page = 1;
      this.getCustomerSubscriptions();
    },
    onCustomerPageChanged(page) {
      if (page < 1 || page > this.totalCustomerPages) return;
      this.customerQueries.page = page;
      this.getCustomerSubscriptions();
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
    getMonthsBetween(date1, date2) {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      const months = (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
      return Math.max(0, months);
    },
    editCustomerSubscription(customer) {
      // Navigate to edit page or open modal
      // eslint-disable-next-line no-console
      console.log('Edit customer subscription', customer);
    },
    viewInStripe(customer) {
      // Open Stripe dashboard or customer page
      if (customer.raw?.stripe_customer_id) {
        const stripeUrl = `https://dashboard.stripe.com/customers/${customer.raw.stripe_customer_id}`;
        window.open(stripeUrl, '_blank');
      } else {
        // eslint-disable-next-line no-console
        console.log('No Stripe customer ID found', customer);
      }
    },
  },
};
</script>
